import { computed, Injectable, Injector } from "@angular/core";
import {
  AttachedMedia,
  AttachedMediaFile,
  Comment,
  CommentCodeData,
  CommentLocation,
  Note,
  TocItemScope,
  TocItemType
} from "@orion2/models/tocitem.models";
import { UniversalFile } from "@orion2/models/file.models";
import { TocItemService } from "@viewer/core";
import { DBConnexionType } from "@viewer/core/pouchdb/types";
import { getDisplayedRevision } from "@orion2/utils/front.utils";
import { v4 as uuidv4 } from "uuid";
import packageJSON from "@package-json";
import { DbItem, XmlDoc } from "@orion2/models/couch.models";
import xpath from "xpath";
import { reaction } from "mobx";
import { CommentEditComponent } from "@viewer/toc-items/comments-module/comment-edit/comment-edit.component";
import { toSignal } from "@angular/core/rxjs-interop";
import { MimeType } from "@orion2/models/enums";
import { AttachedMediaService } from "@viewer/core/toc-items/attached-media.service";
import { DEFAULT_ORION_SETTINGS } from "@orion2/utils/constants.utils";

const SEQ_NUMBER_LENGTH = 5;

@Injectable({
  providedIn: "root"
})
export class CommentService extends TocItemService {
  protected tiType = TocItemType.COMMENT;
  protected tiTarget = DBConnexionType.LOCAL;
  protected tiScope = TocItemScope.CORPORATE;
  // A Map containing the information used to display comments.
  public commentInfoMap = new Map<string, Partial<Comment>>();
  // codeProperties keys are the name of comment code fields, the values are the max length allowed
  public readonly codeProperties = {
    model: "14",
    cageCode: "5",
    year: "4",
    seqNumber: "5",
    commentType: "1"
  };
  private readonly commentEditComponents = new Map<string, CommentEditComponent>();
  private readonly xmlSerializer = new XMLSerializer();
  private readonly domParser = new DOMParser();

  // eslint-disable-next-line @typescript-eslint/naming-convention
  private readonly _allComments = toSignal<Comment[]>(this.tocItemsOfType);
  public readonly allComments = computed<Comment[]>(() => {
    if (!this._allComments()) {
      this.refresh();
      return [];
    }

    const comments = [...this._allComments()];
    this.sortTocItemList(comments);
    this.checkDuplicateCodes(comments);
    return comments;
  });

  public canAttachMedia = DEFAULT_ORION_SETTINGS["notes"].canAttachMedia;

  constructor(
    protected injector: Injector,
    private readonly attachedMediaService: AttachedMediaService
  ) {
    super(injector);
    reaction(
      () => ({
        pubId: this.store.publicationID,
        pubRev: this.store.publicationRevision
      }),
      (state: { pubId: string; pubRev: string }) => {
        if (this.store.isGov && state.pubId && state.pubRev) {
          this.commentInfoMap.clear();
          this.pouchService.xmlCaller.get("loap").then((loap: DbItem) => {
            const loapXML: Document = this.domParser.parseFromString(loap["data"], MimeType.XML);
            this.setPmInfos(loapXML);
            this.setPubInfos(loapXML);
            this.setAppInfos();
          });
        }
      },
      { fireImmediately: true }
    );

    reaction(
      () => this.store.noteSettings,
      () => {
        this.canAttachMedia = this.store.noteSettings?.canAttachMedia;
      },
      { fireImmediately: true }
    );
  }

  /**
   * Initiates a new comment from the given location (DM,PM,APP,PUB).
   * We construct the comment code from the most recent comment by getting model, cageCode, and year and increment the seqNumber of this most recent comment
   * The default priority is cp01 (ROUTINE)
   * The type is "q" (QUERY)
   * @param location
   * @param note
   */
  public async initComment(location: CommentLocation, note?: Note): Promise<void> {
    if (!this._tocItemsOfType) {
      await this.getItemsOfType();
    }
    const commentCodedata = this.initCommentCodeData(this.allComments());
    const commentCode = this.constructCommentCode(commentCodedata);
    const newComment: Comment = {
      _id: `${this.tiType}_${uuidv4()}`,
      code: commentCode,
      fromNote: !!note,
      title: note?.customTitle ?? "",
      contents: [note?.content ?? ""],
      date: new Date(),
      type: this.tiType,
      author: this.store.user.userName,
      revision: this.store.publicationRevision,
      userId: this.store.user.samAccountName,
      locationData: "",
      reference: "",
      priority: "cp01",
      location,
      ...commentCodedata
    };
    await this.setLocationInfos(newComment, location, note?.dmc ?? this.store.currentDMC);
    if (this.canAttachMedia && note?.medias?.length) {
      newComment.medias = await Promise.all(
        note.medias.map((media: AttachedMedia) =>
          this.attachedMediaService
            .getAttachedMediaFile(media.id, media.title, note.status)
            .then((attachedMedia: AttachedMediaFile) =>
              this.attachMediaToComment(attachedMedia.file, newComment, false, true)
            )
        )
      );
    }

    if (
      location === CommentLocation.DM &&
      (!newComment.fromNote || newComment.dmInfos?.dmc === this.store.currentDMC)
    ) {
      this._tocItemsForTarget?.unshift(newComment);
      this.updateTocItemForTargetToNext();
    }
    this._tocItemsOfType.unshift(newComment);
    this.updateTocItemOfTypeToNext();
  }

  /**
   * Cancels the creation of the latest comment.
   * This is useful if we are creating APP/PUB/INFOSET comments from a DM, but
   * navigation to the comments page fails.
   * This can happen when the notes/comments guard blocks it because something
   * has not been saved yet.
   * In this case, we need to cancel the creation of the latest comment.
   */
  public cancelInitComment(): void {
    this._tocItemsOfType.shift();
    this.updateTocItemOfTypeToNext();
  }

  /**
   * Sets location information on the comment.
   * Those are useful when exporting to XML.
   * @private
   * @param {Comment} comment the comment to which location-dependent data should be added.
   * @param {CommentLocation} location the comment's location (APP, PUB, PM, DM).
   * @param {string} reference comment's location used for PM location
   * @returns {void}
   */
  private async setLocationInfos(
    comment: Comment,
    location: CommentLocation,
    dmc: string
  ): Promise<void> {
    switch (location) {
      case CommentLocation.DM: {
        const { locationData, dmInfos, reference } = await this.getDMInfos(dmc);
        comment.locationData = locationData;
        comment.dmInfos = dmInfos;
        comment.reference = reference;
        break;
      }
      case CommentLocation.PM: {
        const reference = this.store.duObject.xml
          ?.querySelector("WP6PmInformation > parent")
          .getAttribute("title");
        const { locationData, pmInfos } = this.commentInfoMap.get(reference);
        comment.locationData = locationData;
        comment.pmInfos = pmInfos;
        comment.reference = reference;
        break;
      }
      case CommentLocation.PUB: {
        const { locationData, pubInfos, reference } = this.commentInfoMap.get("publication");
        comment.locationData = locationData;
        comment.pubInfos = pubInfos;
        comment.reference = reference;
        break;
      }
      case CommentLocation.APP: {
        const { locationData, appInfos, reference } = this.commentInfoMap.get("application");
        comment.locationData = locationData;
        comment.appInfos = appInfos;
        comment.reference = reference;
        break;
      }
      default:
        break;
    }
  }

  /**
   * Checks if the current user can manage the given comment.
   *
   * SPEC: Basic users can only manage their own comments.
   * Admins/Managers can also manage comments from others.
   *
   * @param {Comment} comment The comment for which to check the user rights.
   * @returns {boolean} `true` if the current user can manage it, `false` otherwise.
   */
  public canManage(comment: Comment): boolean {
    return comment.author === this.store.user.userName || this.store.user.canManageAllComments;
  }

  /**
   * Deletes a given list of comments.
   * @public
   * @param {Comment[]} comments
   * @returns {Promise<boolean[]>}
   */
  public deleteComments(comments: Comment[]): Promise<boolean[]> {
    return Promise.all(comments.map((comment: Comment) => this.deleteComment(comment)));
  }

  /**
   * Initialises a comment from the given list of comments.
   * The newly initiated comment have:
   * - the same model and cageCode as the most recently updated comment;
   * - the highest seqNumber of the comments with these data from the current year.
   *
   * @public
   * @param {Comment[]} comments the list of comments.
   * @returns {CommentCodeData} a comment with basic data.
   */
  private initCommentCodeData(comments: Comment[]): CommentCodeData {
    // last updated comment (can be undefined)
    const latestComment = comments?.reduce((latest: Comment, currentComment: Comment) => {
      return !latest || new Date(currentComment.lastUpdate) > new Date(latest.lastUpdate)
        ? currentComment
        : latest;
    }, undefined);

    const currentYear = new Date().getFullYear().toString();

    const baseComment = comments
      ?.filter(
        (comment: Comment) =>
          comment.model === latestComment.model &&
          comment.cageCode === latestComment.cageCode &&
          comment.year === currentYear
      )
      ?.reduce(
        (ref: Comment, currentComment: Comment) =>
          !ref || currentComment.seqNumber > ref.seqNumber ? currentComment : ref,
        undefined
      );

    return {
      model: latestComment?.model || this.store.pubInfo.model,
      cageCode: latestComment?.cageCode || "F0210",
      year: currentYear,
      seqNumber: String(Number(baseComment?.seqNumber || "00000") + 1).padStart(
        SEQ_NUMBER_LENGTH,
        "0"
      ),
      commentType: "q"
    };
  }

  /**
   * Allows you to construct the code for a comment.
   * @public
   * @param {CommentCodeData} codeData Data source retrieved when adding a comment.
   * @returns {string} The formatted code.
   */
  public constructCommentCode(codeData: CommentCodeData): string {
    return Object.keys(this.codeProperties)
      .map((property: string) => codeData[property]?.toUpperCase())
      .join("-");
  }

  /**
   * Allows you to retrieve additionnal data for a datamodule comment.
   * xmlData is necessary for the export of comment.
   * dmInfos is useful for the expand section of the comment.
   * @private
   * @param {string} dmc The DMC of which we want to get infos.
   * @returns {Partial<Comment>} Additional information for a datamodule comment.
   */
  private async getDMInfos(dmc: string): Promise<Partial<Comment>> {
    const duMeta = this.store.getDUMeta(dmc);
    let duXml = this.store.duObject?.xml;
    if (dmc !== this.store.currentDMC) {
      duXml = await this.pouchService.xmlCaller
        .get(dmc)
        .then((xmlDoc: XmlDoc) => this.domParser.parseFromString(xmlDoc.data, MimeType.XML));
    }
    const dmAddress = duXml.querySelector("dmAddress");

    // Allows you to format the XML for export.
    const xmlData = `<dmRefGroup>
          ${this.xmlSerializer
            .serializeToString(dmAddress)
            .replace(/dmAddress/g, "dmRef")
            .replace(/dmIdent/g, "dmRefIdent")
            .replace(/dmRefItems/g, "dmRefAddressItems")}
        </dmRefGroup>`;

    return {
      dmInfos: {
        dmc: dmc ?? "-",
        dmTitle: duMeta?.shortTitle ?? "-",
        dmIssueInfos: [
          duMeta?.issueNumber ?? "-",
          duMeta?.revision.toUpperCase() ?? "-",
          duMeta?.issueDate ?? "-"
        ].join(", ")
      },
      locationData: xmlData,
      reference: duMeta.reference
    };
  }

  /**
   * Sets information of the Infoset from LOAP
   * @public
   * @param {Document} loapXML LOAP content.
   * @returns {void}
   */
  public setPmInfos(loapXML: Document): void {
    const pmRefs = xpath.select("//pmRef", loapXML) as Node[];

    pmRefs.forEach((pmRef: Node) => {
      const pmCode = xpath.select1(".//pmCode", pmRef) as Element;

      const pmTitle: string = xpath
        .select1("string(./pmRefAddressItems/pmTitle)", pmRef)
        .toString();

      // Allows you to format the XML for export.
      const xmlData = `<pmRefGroup>
            ${this.xmlSerializer.serializeToString(pmRef).replace(/pmIdent/g, "pmRefIdent")}
          </pmRefGroup>`;

      const pmValue: Partial<Comment> = {
        locationData: xmlData,
        pmInfos: {
          pmCode:
            [
              pmCode.getAttribute("modelIdentCode"),
              pmCode.getAttribute("pmIssuer"),
              pmCode.getAttribute("pmNumber"),
              pmCode.getAttribute("pmVolume")
            ].join("-") ?? "-"
        }
      };

      this.commentInfoMap.set(pmTitle, pmValue);
    });
  }

  /**
   * Sets information of the Publication from LOAP.
   * @public
   * @param {Document} loapXML LOAP content.
   * @returns {void}
   */
  public setPubInfos(loapXML: Document): void {
    const pmAddress = xpath.select1("//identAndStatusSection/pmAddress", loapXML) as Node;

    // Allows you to format the XML for export.
    const xmlData = `<pmRefGroup>
      ${this.xmlSerializer
        .serializeToString(pmAddress)
        .replace(/pmAddress/g, "pmRef")
        .replace(/pmIdent/g, "pmRefIdent")
        .replace(/pmRefItems/g, "pmRefAddressItems")}
        </pmRefGroup>`;

    const issueDateTag = xpath.select1(".//issueDate", pmAddress) as Element;
    const issueDate = [
      issueDateTag.getAttribute("year"),
      issueDateTag.getAttribute("month"),
      issueDateTag.getAttribute("day")
    ].join("-");

    const pubCode: string = [
      this.store.pubInfo.model,
      this.store.pubInfo.revision,
      issueDate,
      this.store.pubInfo.lang
    ]
      .join("_")
      .toUpperCase();

    const pubValue: Partial<Comment> = {
      locationData: xmlData,
      pubInfos: {
        verbatimText: this.store.pubInfo.verbatimText,
        pubCode
      },
      reference: `${this.store.pubInfo.verbatimText}#${getDisplayedRevision(this.store.pubInfo)}#${this.store.pubInfo.lang}`
    };

    this.commentInfoMap.set("publication", pubValue);
  }

  /**
   * Sets information of the Application from LOAP.
   * @public
   * @return {void}
   */
  public setAppInfos(): void {
    const appVersion: string = packageJSON.version;

    const appValue: Partial<Comment> = {
      // Allows you to format the XML for export.
      locationData: this.commentInfoMap
        .get("publication")
        .locationData.replace(
          /<verbatimText>.*<\/verbatimText>/g,
          `<verbatimText>${appVersion}</verbatimText>`
        ),
      appInfos: {
        appName: "ORION2",
        appVersion
      },
      reference: `ORION2-v${packageJSON.version}`
    };
    this.commentInfoMap.set("application", appValue);
  }

  /**
   * Save comment and its attached medias
   * @param comment
   * @param fromNote
   */
  public async saveComment(comment: Comment, fromNote = false): Promise<boolean> {
    const result = await this.save(comment, this.tiScope, true, true);
    if (result) {
      await this.attachedMediaService.updateTocItemsMedias(
        {} as Comment,
        comment,
        fromNote ? TocItemScope.PRIVATE : this.tiScope, // Trick to force media save
        this.tiScope
      );
    }
    return result;
  }

  /**
   * Updates provided comment and its attached medias
   * @param oldComment
   * @param newComment
   * @param fromNote
   */
  public async updateComment(
    oldComment: Comment,
    newComment: Comment,
    fromNote = false
  ): Promise<boolean> {
    const result = await this.update(oldComment, newComment);
    if (result) {
      await this.attachedMediaService.updateTocItemsMedias(
        oldComment,
        newComment,
        fromNote ? TocItemScope.PRIVATE : this.tiScope, // Trick to force media save
        this.tiScope
      );
    }
    this.attachedMediaService.resetCachedFiles();
    return result;
  }

  /**
   * Overrides the update function of the tocItem service.
   * Comments don't have minRevision and maxRevision.
   * Also, comments are always saved with corporate visibility.
   * @param oldComment
   * @param newComment
   * @returns {Promise<boolean>} `true` if update successed, `false` otherwise
   */
  public update(oldComment: Comment, newComment: Comment): Promise<boolean> {
    if (!this._tocItemsOfType) {
      this._tocItemsOfType = [];
    }
    const isOnlyItemOfType = this._tocItemsOfType.length === 1;
    this.removeFromCache(oldComment);
    delete newComment._rev;
    return this.save(newComment, this.tiScope, true, isOnlyItemOfType);
  }

  /**
   * Deletes provided comment and its attached medias
   * @param commentToDelete
   */
  public async deleteComment(commentToDelete: Comment): Promise<boolean> {
    if (!this.canManage(commentToDelete)) {
      return false;
    }

    try {
      const ret = await this.delete(commentToDelete);
      if (!ret) {
        return false;
      }
      const mediaIds = (commentToDelete.medias || []).map((media: AttachedMedia) => media.id);
      if (mediaIds.length) {
        await this.attachedMediaService
          .deleteAttachedMedias(mediaIds, this.tiScope)
          .catch(() =>
            this.translate.instant("tocItem.errorDelete", { tocItemType: TocItemType.MEDIA })
          );
      }
      return true;
    } catch (error) {
      this.messageService.error(
        this.translate.instant("tocItem.errorDelete", { tocItemType: this.tiType })
      );
      return false;
    }
  }

  /**
   * Overrides the delete function of the tocItem service.
   * We do that because comments do not have min and max revisions.
   * The comments are always saved on the corporate visibility.
   * @param {Comment} comment The comment to delete.
   * @param {string} visibility The visibility of the comment (public, corporate...).
   * @param {boolean} updateCache If we should update the cache (`true` by default).
   * @returns {Promise<boolean>} `true` if deletion was successful, `false` otherwise.
   */
  protected async delete(
    comment: Comment,
    visibility = this.tiScope,
    updateCache = true
  ): Promise<boolean> {
    try {
      await this.getCaller(visibility).delete(comment);

      if (updateCache) {
        this.removeFromCache(comment);
      }
      return true;
    } catch (err) {
      if (err.message === "missing") {
        // whatever the reason, if a comment is found in db
        // we should remove it from tocItem.service caches array
        // and send a success message.
        if (updateCache) {
          this.removeFromCache(comment);
        }
        return true;
      } else {
        console.error(err);
        return false;
      }
    }
  }

  /**
   * Overrides the sortTocItemList function of the tocItem service.
   * Comments need to be grouped by their code and final char in specific order.
   * @param {Comment[]} comments The list of comments to sort.
   * @returns {void}
   */
  public sortTocItemList(comments: Comment[]): void {
    const customOrder = { R: 1, I: 2, Q: 3 };
    const splitRegex = /^(.*)-([^ -]+)$/;
    comments.sort((c1: Comment, c2: Comment) => {
      // _1 and _2 are unused cause string.match return [full string, substring1, substring2, ...]
      const [_1, main1, type1] = c1.code.match(splitRegex);
      const [_2, main2, type2] = c2.code.match(splitRegex);
      const res = main2.localeCompare(main1);
      if (!res) {
        return customOrder[type1] - customOrder[type2];
      }
      return res;
    });
  }

  /**
   * Adds a CommentEditComponent to the list if absent.
   * @param {CommentEditComponent} commentEditComponent
   * @returns {void}
   */
  public addCommentEditComponent(commentEditComponent: CommentEditComponent): void {
    this.commentEditComponents.set(commentEditComponent.comment()._id, commentEditComponent);
  }

  /**
   * Removes a CommentEditComponent from list if present.
   * @param {CommentEditComponent} commentEditComponent
   * @returns {void}
   */
  public removeCommentEditComponent(commentEditComponent: CommentEditComponent): void {
    this.commentEditComponents.delete(commentEditComponent.comment()._id);
  }

  /**
   * Checks before leaving the page if we have some comments have been edited
   * but not saved.
   *
   * @returns A boolean that is `true` if no comment have been edited without
   * saving, `false` otherwise.
   */
  public canDeactivate(): boolean {
    return !this.commentEditComponents.size;
  }

  /**
   * Saves all comments in edition.
   *
   * @returns A promise of a boolean array.
   */
  public saveAll(): Promise<boolean[]> {
    return Promise.all(
      Array.from(this.commentEditComponents.values()).map(
        (commentEditComponent: CommentEditComponent) => commentEditComponent.save()
      )
    );
  }

  /**
   * Cancels all comments in edition.
   *
   * @returns A promise of a boolean array.
   */
  public cancelAll(): Promise<boolean[]> {
    return Promise.all(
      Array.from(this.commentEditComponents.values()).map(
        (commentEditComponent: CommentEditComponent) => commentEditComponent.cancel()
      )
    );
  }

  /**
   * Check if a comment code is already used on other comment
   * @param commentId comment's _id to check
   * @param commentCode comment's code to check
   * @returns A promise of a boolean.
   */
  public isDuplicateCode(commentId: string, commentCode: string): boolean {
    return this._allComments()?.some(
      (comment: Comment) => commentId !== comment._id && commentCode === comment.code
    );
  }

  /**
   * Check if a comments list have duplicate codes compared with the entire list of comments
   * Keep the duplicated codes in service to use it in comment component and display in red these codes
   * Display a message if some comments have duplicate codes
   * @param comments a list of comments to check
   */
  public checkDuplicateCodes(commentList: Comment[]): void {
    const listHasDuplicateCode = commentList.some((comment: Comment) =>
      this.isDuplicateCode(comment._id, comment.code)
    );

    if (listHasDuplicateCode) {
      this.messageService.warning(this.translate.instant("comments.code.warning"));
    }
  }

  /**
   * Push the media to the current comment
   * Keep in memory the file for push it in database when saving the comment
   * @param file
   * @param comment
   * @param scope
   * @param isImport
   */
  public async attachMediaToComment(
    file: File | UniversalFile,
    comment: Comment,
    isImport = false,
    fromNote = false
  ): Promise<AttachedMedia> {
    return this.attachedMediaService.createAttachedMedia(
      file,
      this.tiScope,
      comment.title,
      isImport,
      fromNote
    );
  }

  /**
   * Open mediaId in new browser tab
   * Download the file for apps (cordova, electron), filename is mediaTitle
   * @param mediaId
   * @param mediaTitle
   */
  public openMedia(mediaId: string, mediaTitle: string) {
    this.attachedMediaService.open(mediaId, mediaTitle, this.tiScope).catch(err => {
      console.error(err);
    });
  }

  /**
   * Get the file mediaId from uploaded files
   * If it's not found we get it from DB
   * @param mediaId
   * @param mediaTitle
   */
  public getAttachedMedia(mediaId: string, mediaTitle: string): Promise<AttachedMediaFile> {
    return this.attachedMediaService.getAttachedMediaFile(
      mediaId,
      mediaTitle,
      TocItemScope.CORPORATE
    );
  }
}
